import { queryKeys } from '@/lib/query-client';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  type ScheduledJobsParams,
  type UnscheduledJobsParams,
  getScheduledJobs,
  getUnscheduledJobs,
} from '../api/unscheduledJobsApi';

export function useUnscheduledJobs(params: UnscheduledJobsParams = {}) {
  return useQuery({
    queryKey: [...queryKeys.planner.all, 'unscheduled-jobs', params],
    queryFn: () => getUnscheduledJobs(params),
    staleTime: 10 * 1000,
  });
}

export function useScheduledJobs(params: ScheduledJobsParams = {}) {
  return useQuery({
    queryKey: [...queryKeys.planner.all, 'scheduled-jobs', params],
    queryFn: () => getScheduledJobs(params),
    staleTime: 10 * 1000,
  });
}

/** Filter params for the infinite scheduled-jobs query — paging is driven internally. */
export type ScheduledJobsInfiniteParams = Omit<ScheduledJobsParams, 'limit' | 'skip'>;

const DEFAULT_SCHEDULED_PAGE_SIZE = 200;

/**
 * Auto-paginating variant of {@link useScheduledJobs}. Fetches scheduled jobs one
 * page at a time and accumulates them across pages, automatically fetching every
 * remaining page in the background until all are loaded.
 *
 * Per WRK-5235 (and Steven's feedback) the scheduler must not require the user to
 * manually load pages — if Lite knows more pages exist it loads them itself and
 * simply informs the user it's doing so. `getNextPageParam` returns the next
 * `skip` offset while fewer rows have been loaded than the server reports in
 * `total`, and `undefined` once everything is loaded.
 */
export function useScheduledJobsInfinite(
  params: ScheduledJobsInfiniteParams = {},
  pageSize: number = DEFAULT_SCHEDULED_PAGE_SIZE,
) {
  const query = useInfiniteQuery({
    queryKey: [...queryKeys.planner.all, 'scheduled-jobs-infinite', params, pageSize],
    queryFn: ({ pageParam }) => getScheduledJobs({ ...params, limit: pageSize, skip: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (_lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.data.length, 0);
      const total = allPages[0]?.total ?? 0;
      return loaded < total ? loaded : undefined;
    },
    staleTime: 10 * 1000,
  });

  // Auto-load the next page as soon as the current one settles, so all pages are
  // fetched in the background without any user action. `pagesLoaded` is included
  // in the deps so the effect re-evaluates after every page arrives — otherwise
  // it wouldn't re-fire while `hasNextPage` stays `true` across pages.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const pagesLoaded = query.data?.pages.length ?? 0;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, pagesLoaded]);

  return query;
}
